import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    AlertTriangle,
    Award,
    BarChart3,
    CheckCircle2,
    Clock3,
    Filter,
    Gauge,
    RefreshCcw,
    Star,
    Target,
    X,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    Radar,
    RadarChart,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";

import { fetchCampagnesRequest } from "../../../redux/actions/campagnesActions";
import { fetchSkillEvalsByUserRequest } from "../../../redux/actions/skillEvaluationActions";
import "../rf/DashboardSkillsRespo.css";

const COLORS = {
    primary: "#204385",
    secondary: "#ee645f",
    success: "#4caf50",
    warning: "#ff9800",
    error: "#f44336",
    info: "#2196f3",
    grey: "#757575",
};
const DOMAIN_SCORE_COLORS = [COLORS.primary, COLORS.secondary];

const STATUS_META = {
    NOT_STARTED: { label: "Non demarree", color: COLORS.secondary },
    EN_ATTENTE_COLLAB: { label: "A completer", color: COLORS.warning },
    EN_ATTENTE_MANAGER: { label: "Attente manager", color: COLORS.secondary },
    EN_ATTENTE_N2: { label: "Attente N+2", color: COLORS.info },
    VALIDEE: { label: "Validee", color: COLORS.primary },
    REJETEE: { label: "Rejetee", color: COLORS.error },
    PROPOSE: { label: "Proposee", color: COLORS.grey },
};

const numberOrNull = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const round1 = (value) => Math.round(value * 10) / 10;
const pct = (value, total) => (!total ? 0 : Math.round((value / total) * 100));
const statusCode = (statut) => String(typeof statut === "string" ? statut : statut?.name || "").toUpperCase();

const average = (values) => {
    const nums = (values || []).map(numberOrNull).filter((n) => n !== null);
    if (!nums.length) return null;
    return round1(nums.reduce((sum, n) => sum + n, 0) / nums.length);
};

const getSkillScore = (niveau) =>
    numberOrNull(niveau?.scoreObtenu) ??
    numberOrNull(niveau?.scoreN2) ??
    numberOrNull(niveau?.scoreManager) ??
    numberOrNull(niveau?.scoreCollab);

const getExpectedScore = (niveau) =>
    numberOrNull(niveau?.scoreRequis) ??
    numberOrNull(niveau?.niveauRequis) ??
    numberOrNull(niveau?.niveauAttendu) ??
    numberOrNull(niveau?.scoreAttendu);

function SectionTitle({ icon, children }) {
    return (
        <div className="dsrSectionTitle">
            <span className="dsrSectionTitle__icon">{icon}</span>
            <span>{children}</span>
        </div>
    );
}

function StatCard({ title, value, subtitle, icon, color, onClick }) {
    return (
        <div
            className={`dsrStatCard${onClick ? " dsrStatCard--clickable" : ""}`}
            onClick={onClick}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={(event) => {
                if (onClick && (event.key === "Enter" || event.key === " ")) onClick();
            }}
        >
            <div className="dsrStatCard__row">
                <div>
                    <div className="dsrStatCard__title">{title}</div>
                    <div className="dsrStatCard__value" style={{ color }}>{value}</div>
                    {subtitle ? <div className="dsrStatCard__sub">{subtitle}</div> : null}
                </div>
                <div className="dsrStatIcon" style={{ color, background: `${color}15`, borderColor: `${color}25` }}>
                    {icon}
                </div>
            </div>
        </div>
    );
}

function DetailsModal({ title, subtitle, children, onClose }) {
    return (
        <div className="dsrModalBackdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <div className="dsrModalPanel">
                <div className="dsrModalHeader">
                    <div>
                        <div className="dsrModalTitle">{title}</div>
                        {subtitle ? <div className="dsrModalSubtitle">{subtitle}</div> : null}
                    </div>
                    <button className="dsrModalClose" type="button" onClick={onClose} title="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="dsrModalBody">{children}</div>
            </div>
        </div>
    );
}

function ChartCard({ title, children }) {
    return (
        <div className="dsrChartCard">
            <div className="dsrChartCard__title">{title}</div>
            <div className="dsrChartCard__body">{children}</div>
        </div>
    );
}

export default function DashboardSkillsCollab() {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth || {});
    const { list: campagnesRaw } = useSelector((state) => state.campagnes || { list: [] });
    const { skillEvaluations = [], loading } = useSelector((state) => state.skillEvaluation || {});

    const [campagneId, setCampagneId] = useState("");
    const [detailsModal, setDetailsModal] = useState(null);

    const userId = user?.id || user?.uid || user?.personnelId;

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        if (userId) dispatch(fetchSkillEvalsByUserRequest(userId));
    }, [dispatch, userId]);

    const campagnes = useMemo(() => {
        return (campagnesRaw || [])
            .filter((c) => String(c?.typeCampagne || "").toUpperCase() === "SKILLS" || !c?.typeCampagne)
            .sort((a, b) => (a?.status === b?.status ? 0 : a?.status === true ? -1 : 1));
    }, [campagnesRaw]);

    useEffect(() => {
        if (!campagnes.length) {
            setCampagneId("");
            return;
        }
        if (!campagneId || !campagnes.some((campagne) => String(campagne.id) === String(campagneId))) {
            const defaultCampagne = campagnes.find((campagne) => campagne?.isDefault) || campagnes.find((campagne) => campagne?.status === true) || campagnes[0];
            setCampagneId(String(defaultCampagne?.id || ""));
        }
    }, [campagnes, campagneId]);

    const refreshData = useCallback(() => {
        if (userId) dispatch(fetchSkillEvalsByUserRequest(userId));
    }, [dispatch, userId]);

    const selectedEvaluation = useMemo(() => {
        return (skillEvaluations || []).find((evaluation) => !campagneId || Number(evaluation.campagneId) === Number(campagneId)) || null;
    }, [skillEvaluations, campagneId]);

    const analytics = useMemo(() => {
        const niveaux = selectedEvaluation?.niveaux || [];
        const scored = niveaux
            .map((niveau) => ({
                ...niveau,
                score: getSkillScore(niveau),
                expected: getExpectedScore(niveau),
                collab: numberOrNull(niveau?.scoreCollab),
                manager: numberOrNull(niveau?.scoreManager),
                n2: numberOrNull(niveau?.scoreN2),
            }))
            .filter((niveau) => niveau.score !== null || niveau.collab !== null);

        const finalScored = scored.filter((niveau) => niveau.score !== null);
        const gaps = finalScored
            .filter((niveau) => niveau.expected !== null)
            .map((niveau) => ({ ...niveau, gap: niveau.expected - niveau.score }));

        const evaluated = scored.filter((niveau) => niveau.collab !== null || niveau.score !== null).length;
        const total = niveaux.length;
        const avgScore = average(finalScored.map((niveau) => niveau.score));
        const avgCollab = average(scored.map((niveau) => niveau.collab));
        const avgManager = average(scored.map((niveau) => niveau.manager));
        const avgGap = average(gaps.map((niveau) => niveau.gap));
        const reached = gaps.filter((niveau) => niveau.gap <= 0).length;

        const byDomain = Object.values(
            finalScored.reduce((acc, item) => {
                const name = item.domaineNom || item.domaineName || "Non classe";
                if (!acc[name]) acc[name] = { name, scores: [], gaps: [] };
                acc[name].scores.push(item.score);
                if (item.expected !== null) acc[name].gaps.push(item.expected - item.score);
                return acc;
            }, {})
        ).map((item) => ({ name: item.name, score: average(item.scores) || 0, gap: average(item.gaps) || 0 }));

        const skillRows = finalScored.map((niveau) => ({
            name: niveau.skillNom || niveau.skillCode || "Competence",
            domain: niveau.domaineNom || niveau.domaineName || "-",
            score: niveau.score,
            expected: niveau.expected ?? 0,
            gap: niveau.expected !== null ? niveau.expected - niveau.score : 0,
            collab: niveau.collab,
            manager: niveau.manager,
            n2: niveau.n2,
        }));
        const gapDetails = [...skillRows].filter((item) => item.gap > 0).sort((a, b) => b.gap - a.gap);

        const status = statusCode(selectedEvaluation?.statut);
        const workflow = [
            { name: "Auto-eval", value: status ? 100 : 0, color: COLORS.warning },
            { name: "Manager", value: ["EN_ATTENTE_N2", "VALIDEE"].includes(status) ? 100 : status === "EN_ATTENTE_MANAGER" ? 50 : 0, color: COLORS.secondary },
            { name: "N+2", value: status === "VALIDEE" ? 100 : status === "EN_ATTENTE_N2" ? 50 : 0, color: COLORS.info },
            { name: "Validee", value: status === "VALIDEE" ? 100 : 0, color: COLORS.success },
        ];

        return {
            total,
            evaluated,
            progress: pct(evaluated, total),
            status,
            statusLabel: STATUS_META[status]?.label || status || "Non demarree",
            avgScore,
            avgCollab,
            avgManager,
            avgGap,
            reached,
            criticalCount: gaps.filter((niveau) => niveau.gap >= 1).length,
            byDomain,
            radarData: skillRows.slice(0, 10),
            gapDetails,
            weakSkills: gapDetails.slice(0, 5),
            strongSkills: [...skillRows].sort((a, b) => b.score - a.score).slice(0, 5),
            workflow,
        };
    }, [selectedEvaluation]);

    return (
        <div className="dsrPage">
            <header className="dsrHeader">
                <div>
                    <h1>Mon tableau de bord</h1>
                </div>
                <div className="dsrActions">
                    <div className="dsrSelectWrap">
                        <Filter size={15} />
                        <label htmlFor="dsc-campagne">Campagne</label>
                        <select id="dsc-campagne" className="dsrSelect" value={campagneId} onChange={(event) => setCampagneId(event.target.value)}>
                            <option value="">Selectionnez une campagne</option>
                            {campagnes.map((campagne) => (
                                <option key={campagne.id} value={campagne.id}>{campagne.nom || campagne.name || `Campagne ${campagne.id}`}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </header>

            <main className="dsrMain">
                <SectionTitle icon={<Gauge size={18} />}>Mon avancement</SectionTitle>
                <div className="dsrGrid dsrGrid--4">
                    <StatCard title="Progression de mon auto-évaluation" value={`${analytics.progress}%`} icon={<Target size={20} />} color={COLORS.primary} />
                    <StatCard title="Score moyen" value={analytics.avgScore ?? "-"} icon={<Award size={20} />} color={COLORS.secondary} />
                    <StatCard title="Écart moyen" value={analytics.avgGap ?? "-"} icon={<AlertTriangle size={20} />} color={analytics.avgGap > 0 ? COLORS.warning : COLORS.success} onClick={() => setDetailsModal("personal-gaps")} />
                    <StatCard title="Niveau attendu atteint" value={analytics.reached} icon={<CheckCircle2 size={20} />} color={COLORS.success} />
                </div>

                <SectionTitle icon={<BarChart3 size={18} />}>Mes résultats</SectionTitle>
                <div className="dsrGrid dsrGrid--2">
                    <ChartCard title="Radar de mes compétences">
                        <ResponsiveContainer width="100%" height={330}>
                            <RadarChart data={analytics.radarData}>
                                <PolarGrid />
                                <PolarAngleAxis dataKey="name" tick={{ fontSize: 10 }} />
                                <PolarRadiusAxis angle={30} domain={[0, 4]} />
                                <Radar name="Obtenu" dataKey="score" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.25} />
                                <Radar name="Requis" dataKey="expected" stroke={COLORS.secondary} fill={COLORS.secondary} fillOpacity={0.12} />
                                <RechartsTooltip />
                            </RadarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Score moyen par domaine">
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={analytics.byDomain}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" interval={0} tick={{ fontSize: 11 }} />
                                <YAxis domain={[0, 4]} />
                                <RechartsTooltip />
                                <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                                    {analytics.byDomain.map((entry, index) => (
                                        <Cell key={entry.name} fill={DOMAIN_SCORE_COLORS[index % DOMAIN_SCORE_COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                <SectionTitle icon={<Target size={18} />}>Mon plan de développement</SectionTitle>
                <div className="dsrGrid dsrGrid--2">
                    <div className="dsrPanel">
                        <div className="dsrPanel__title">Compétences à renforcer</div>
                        <div className="dsrPanel__body">
                            {analytics.weakSkills.length ? (
                                <div className="dsrRiskList">
                                    {analytics.weakSkills.map((skill) => (
                                        <div className="dsrRiskItem" key={skill.name}>
                                            <div>
                                                <div className="dsrRiskTitle">{skill.name}</div>
                                                <div className="dsrRiskMeta">Obtenu {skill.score} - requis {skill.expected || "-"}</div>
                                            </div>
                                            <span className="dsrBadge dsrBadge--danger">+{skill.gap}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : <div className="dsrEmpty">Aucun écart prioritaire</div>}
                        </div>
                    </div>

                    <div className="dsrPanel">
                        <div className="dsrPanel__title">Mes points forts</div>
                        <div className="dsrPanel__body">
                            {analytics.strongSkills.length ? (
                                <div className="dsrRiskList">
                                    {analytics.strongSkills.map((skill) => (
                                        <div className="dsrRiskItem" key={skill.name}>
                                            <div>
                                                <div className="dsrRiskTitle">{skill.name}</div>
                                                <div className="dsrRiskMeta">Manager {skill.manager ?? "-"} - auto-eval {skill.collab ?? "-"}</div>
                                            </div>
                                            <span className="dsrBadge dsrBadge--success">{skill.score}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : <div className="dsrEmpty">Aucune donnee disponible</div>}
                        </div>
                    </div>
                </div>
            </main>

            {detailsModal === "personal-gaps" && (
                <DetailsModal
                    title="Détails de mes écarts"
                    subtitle={`${analytics.gapDetails.length} competence(s) avec un niveau requis superieur au niveau obtenu`}
                    onClose={() => setDetailsModal(null)}
                >
                    {analytics.gapDetails.length ? (
                        <div className="dsrTableWrap">
                            <table className="dsrTable">
                                <thead>
                                <tr>
                                    <th>Competence</th>
                                    <th>Domaine</th>
                                    <th>Auto-eval</th>
                                    <th>Manager</th>
                                    <th>N+2</th>
                                    <th>Requis</th>
                                    <th>Obtenu</th>
                                    <th>Écart</th>
                                </tr>
                                </thead>
                                <tbody>
                                {analytics.gapDetails.map((row, index) => (
                                    <tr key={`${row.name}-${index}`}>
                                        <td>{row.name}</td>
                                        <td>{row.domain}</td>
                                        <td>{row.collab ?? "-"}</td>
                                        <td>{row.manager ?? "-"}</td>
                                        <td>{row.n2 ?? "-"}</td>
                                        <td>{row.expected || "-"}</td>
                                        <td>{row.score}</td>
                                        <td><span className="dsrBadge dsrBadge--danger">+{row.gap}</span></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="dsrEmpty">Aucun écart a afficher pour cette campagne.</div>
                    )}
                </DetailsModal>
            )}
        </div>
    );
}
